import React from 'react';

import { isLightColor } from '../../../lib/color';
import { Fill, Spinner } from './PreviewPlaceholder.styles';

/** The animated Whitelabels.com loader SVG — the same one the app boots with. */
export const PREVIEW_LOADER_SRC = 'https://micros-t.b-cdn.net/whitelabels.com-loader-1775466124392.svg';

export type PreviewPlaceholderProps = {
  /** Shown (opaque) while the preview iframe loads; fades out once revealed. */
  shown: boolean;
  /** Brand body background (solid) — the flat colour the whole screen fills with. */
  bgColor?: string;
  /** Whether to run the animated loader. Default true (something is actively loading).
   *  Pass false for a SETTLED empty state — a flat brand fill with no spinner — when
   *  there is nothing to wait for (e.g. no static screenshot exists for the selection),
   *  so the surface doesn't read as perpetually stuck loading a preview that never comes. */
  spinner?: boolean;
  /** Where the loader sits in the screen cut-out. Default 'center'; pass 'top' when the
   *  device is taller than the visible area (the mobile design-flow phone, whose lower
   *  half sits under the bottom sheet) so the loader isn't masked off-screen. */
  spinnerAlign?: 'center' | 'top';
};

/**
 * Full-bleed loading placeholder for a device-frame preview: a single opaque, solid
 * brand-coloured surface that covers the entire screen cut-out, with the animated
 * Whitelabels.com loader centred at 64x64. A flat colour (no gradient) keeps the
 * surface from flickering when the device is switched. The loader fades in on appear
 * and out on reveal, and the whole surface cross-fades out as the iframe fades in.
 * Drop it into a `position: relative/absolute` device screen.
 */
export const PreviewPlaceholder = ({
  shown,
  bgColor,
  spinner = true,
  spinnerAlign = 'center',
}: PreviewPlaceholderProps): React.ReactElement => {
  // Fade the loader IN one frame after it appears (mounting at opacity 1 would pop).
  const [loaderIn, setLoaderIn] = React.useState(false);
  React.useEffect(() => {
    if (!shown || !spinner) {
      setLoaderIn(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setLoaderIn(true));
    return () => cancelAnimationFrame(raf);
  }, [shown, spinner]);

  // The loader art is white (for dark screens); on a light brand fill, darken it so
  // it stays legible whatever the brand colour is.
  const onLight = isLightColor(bgColor);

  return (
    <Fill $shown={shown} $bg={bgColor} aria-hidden>
      {spinner ? (
        <Spinner $shown={loaderIn} $onLight={onLight} $align={spinnerAlign} src={PREVIEW_LOADER_SRC} alt="" />
      ) : null}
    </Fill>
  );
};

export default PreviewPlaceholder;
